import { inject, Injectable } from '@angular/core'
import { IAutoQuestionFormDefaults } from '@employer/app/components/jobs-automation/automation-auto-question/auto-questions.model'
import { IAutomationDefaultMessages, IAutomationFormData } from '@employer/app/components/jobs-automation/automation-form.model'
import { selectors } from '@employer/app/store/selectors'
import { environment } from '@employer/environments/environment'
import { isNotNil } from '@engineering11/utility'
import { E11Logger } from '@engineering11/web-api-error'
import { RestApiClient, VersionType } from '@engineering11/web-api-rest'
import { Store } from '@ngrx/store'
import { filter, map, Observable, tap } from 'rxjs'
import { getBaseSuffix } from 'shared-lib'

@Injectable({ providedIn: 'root' })
export class JobDefaultAutomationService {
  #logger = inject(E11Logger)
  #store = inject(Store)
  #restClient = new RestApiClient({
    baseUrl: environment.services.jobsAutomation,
    token: this.#store.select(selectors.getCurrentToken).pipe(filter(isNotNil)),
    versionType: VersionType.Header,
    baseSuffix: getBaseSuffix(environment, 'jobsAutomation'),
  })

  getAutomationDefaultMessageForJob(): Observable<IAutomationDefaultMessages> {
    this.#logger.log('getAutomationDefaultMessageForJob ')
    return this.#restClient.get<IAutomationDefaultMessages>(`jobs-automation/message-defaults`, {}).pipe(
      tap(a => this.#logger.log('getAutomationDefaultMessageForJob: ', a)),
      map(res => res.data)
    )
  }

  static applyDefaultAutomationMessages(defaultMessage: IAutomationDefaultMessages, automationFormDefaults: IAutomationFormData) {
    return {
      ...automationFormDefaults,
      autoResponse: {
        ...automationFormDefaults.autoResponse,
        message: defaultMessage.autoResponseMessage,
      },
      requestMore: {
        ...automationFormDefaults.requestMore,
        initialMessage: {
          ...automationFormDefaults.requestMore.initialMessage,
          message: defaultMessage.requestUpdatesMessage,
        },
        followUp: {
          ...automationFormDefaults.requestMore.followUp,
          message: defaultMessage.requestUpdatesFollowUpMessage,
        },
        warning: {
          ...automationFormDefaults.requestMore.warning,
          message: defaultMessage.requestUpdatesWarningMessage,
        },
      },
      ...(automationFormDefaults.cultureIndex && {
        cultureIndex: {
          ...automationFormDefaults.cultureIndex,
          initialMessage: {
            ...automationFormDefaults.cultureIndex.initialMessage,
            message: defaultMessage.cultureIndexMessage,
          },
          followUp: {
            ...automationFormDefaults.cultureIndex.followUp,
            message: defaultMessage.cultureIndexFollowUpMessage,
          },
          warning: {
            ...automationFormDefaults.cultureIndex.warning,
            message: defaultMessage.cultureIndexWarningMessage,
          },
        },
      }),
    }
  }

  static applyDefaultAutoQuestionMessages(defaultMessage: IAutomationDefaultMessages, autoQuestionFormDefaults: IAutoQuestionFormDefaults) {
    const { initialMessage, followUp, warning, ...restDefaults } = autoQuestionFormDefaults

    return {
      ...restDefaults,
      initialMessage: {
        ...initialMessage,
        message: defaultMessage.autoQuestionMessage,
      },
      followUp: {
        ...followUp,
        message: defaultMessage.autoQuestionFollowUpMessage,
      },
      warning: {
        ...warning,
        message: defaultMessage.autoQuestionWarningMessage,
      },
    }
  }
}
